import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

import CheckIcon from '../../svgs/CheckIcon';
import ChevronDownIcon from '../../svgs/ChevronDownIcon';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import { PANEL_FIELD_CSS } from './panelFieldStyles';

import type { KeyboardEvent } from 'react';

type Props = {
  values: { caption: string; value: string | null }[];
  onChange: (value: string | null) => void;
  defaultValue: string | undefined | null;
  value: string;
  /** Names the field for assistive tech when no `<label>` points at it. */
  ariaLabel?: string;
  disabled?: boolean;
};

/**
 * The side panel's single-select dropdown (Element Animation, Enter / Exit
 * Animation, a long Fit list). The closed trigger is the panel field box
 * (`PANEL_FIELD_CSS`) with a muted 10px chevron that turns when open; the menu
 * is the standard rounded-row menu with a gold check on the current value
 * (docs/design-system.md § "Panel Fields (Element Settings)").
 *
 * It was a `div` the keyboard could not reach, 36px with no fill and no hover
 * or focus, with a gold chevron that never turned and a green circle on the
 * current row (PLAT-12137). Now: Enter / Space / ArrowDown open it on the
 * current value, the arrows move through the rows, Escape closes it and hands
 * focus back to the trigger.
 */
const PropertyDropdown = (props: Props) => {
  const [selectedItem, setSelectedItem] = useState<string | undefined | null>(
    props.defaultValue,
  );
  const [open, setOpen] = useState<boolean>(false);
  const listId = useId();

  useEffect(() => {
    setSelectedItem(props.defaultValue);
  }, [props.defaultValue]);

  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(contentRef, () => setOpen(false));

  // Open on the current value: bring the menu into view without jumping the
  // panel ('nearest', not 'start'), then focus the selected row.
  useEffect(() => {
    if (!open) return;
    listRef.current?.scrollIntoView?.({ block: 'nearest' });
    const rows =
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]');
    if (!rows?.length) return;
    const current = Array.from(rows).find(
      r => r.getAttribute('aria-selected') === 'true',
    );
    (current ?? rows[0]).focus();
  }, [open]);

  const selectedValue = props.values.find(v => v.value === selectedItem);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (value: string | null) => {
    setSelectedItem(value);
    close();
    props.onChange(value);
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ??
        [],
    );
    const index = rows.findIndex(r => r === document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      rows[Math.min(rows.length - 1, index + 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      rows[Math.max(0, index - 1)]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <Main ref={contentRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={props.ariaLabel}
        disabled={props.disabled}
        $open={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleTriggerKeyDown}
      >
        <SelectedText>{selectedValue?.caption || ''}</SelectedText>
        <Chevron $open={open} aria-hidden="true">
          <ChevronDownIcon />
        </Chevron>
      </Trigger>
      {open && (
        <Menu
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={props.ariaLabel}
          onKeyDown={handleListKeyDown}
        >
          {props.values.map(item => {
            const selected = selectedItem === item.value;
            return (
              <Row
                key={item.value ?? 'null'}
                type="button"
                role="option"
                aria-selected={selected}
                $selected={selected}
                onClick={() => choose(item.value)}
              >
                <RowLabel>{item.caption}</RowLabel>
                {selected && (
                  <Check aria-hidden="true">
                    <CheckIcon
                      width="10"
                      height="10"
                      fillColor="var(--accent)"
                    />
                  </Check>
                )}
              </Row>
            );
          })}
        </Menu>
      )}
    </Main>
  );
};

export default PropertyDropdown;

const Main = styled.div`
  position: relative;
  min-width: 0;
`;

const Trigger = styled.button<{ $open: boolean }>`
  ${PANEL_FIELD_CSS}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  text-align: left;
  cursor: pointer;

  /* Open reads as focused: the field is what the menu belongs to. */
  ${props =>
    props.$open &&
    `
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  `}
`;

const SelectedText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  flex-shrink: 0;
  color: var(--text-muted);
  transform: rotate(${props => (props.$open ? '180deg' : '0deg')});
  transition: transform var(--transition-fast);
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  padding: var(--spacing-xs);
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--elevation-toast);
`;

// The standard menu row (design system § "Menu-item styling"): 32px, 10px
// padding, 6px radius, secondary text that brightens on hover, and the current
// value in gold with its check.
const Row = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  width: 100%;
  min-height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${props =>
    props.$selected ? 'var(--accent)' : 'var(--text-secondary)'};
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-align: left;
  cursor: pointer;
  outline: none;

  &:hover,
  &:focus-visible {
    background: var(--surface-elevated);
    color: ${props =>
      props.$selected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

const RowLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Check = styled.span`
  display: flex;
  flex-shrink: 0;
`;
